import useParamsType from 'src/hooks/useParamsType.hook'
import {
  CouponDetailResponseType,
  PaymentOSType,
  PaymentWindowRequestCreditCardSpace,
  PaymentWindowResponseSpace,
  PGType,
} from 'src/types/api'
import useCustomCookies from 'src/hooks/useCustomCookies.hook'
import { decryptPhoneNumber, detectDevice, limitStringByBytes } from 'src/utils'
import { DateUtils } from 'src/utils/date'
import { useCookies } from 'react-cookie'
import usePageNav from 'src/hooks/usePageNav.hook'
import {
  paymentWindowResponseQueryOption,
  useCustomMutation,
} from 'src/hooks/react-query/useCustomMutation.hook'
import { useChannel } from 'src/provider/ChannelContextProvider'
import { addFlowBreadcrumb } from 'src/utils/sentry/breadcrumbs'

interface PaymentDataProps {
  pg: PGType
  payMethod: string
  itemNameSlice: boolean
  orderId: string
  couponData: CouponDetailResponseType
  isGift?: boolean
  /**
   * 주문 생성 시각(YYYYMMDDHHmmss). CmdType 100/2011 요청과 결제창 form이 동일한 값을 써야 한다.
   * 호출 시점마다 새로 만들면 HashKey 검증이 깨지므로 여기서 생성하지 않는다.
   */
  orderDate?: string
}

const payTypeToServiceId = [
  { id: 'CARD', value: process.env.REACT_APP_PAYMENT_CARD_SERVICE_ID as string },
  { id: 'MOBILE', value: '' },
  { id: 'KAKAOPAY', value: process.env.REACT_APP_PAYMENT_KAKAO_SERVICE_ID as string },
  { id: 'SAMSUNGPAY', value: process.env.REACT_APP_PAYMENT_SAMSUNG_SERVICE_ID as string },
  { id: 'TMONEY', value: process.env.REACT_APP_PAYMENT_TMONEY_SERVICE_ID as string },
  { id: 'NAVERPAY', value: process.env.REACT_APP_PAYMENT_NAVER_SERVICE_ID as string },
  { id: 'APPLEPAY', value: process.env.REACT_APP_PAYMENT_APPLE_SERVICE_ID as string },
]

/**
 * 결제창: 빌게이트(카드, 삼성페이, 카카오페이, 티머니), 다날(카드, 삼성페이, 카카오페이, 모바일소액결제, 페이코)
 * PayMethod: 결제 수단 (CARD, SAMSUNGPAY, KAKAOPAY, TMONEY, MOBILE, PAYCO)
 * PAY_TYPE: billgate 간편결제 타입 (KAKAOPAY, NAVERPAY, SAMSUNGPAY, APPLEPAY)
 * SERVICE_CODE: billgate 서비스코드(신용카드:0900, 티머니:1600)
 */

const usePayment = () => {
  const { couponIdx, couponType, couponNumber, pNumber, pay_channel, exception_Idx } =
    useParamsType()
  const { couponPaymentCancelPageNav, couponPaymentResultPageNav, giftPaymentCancelPageNav } =
    usePageNav()
  const { now, DateFormat } = DateUtils
  const { c_tel } = useCustomCookies()
  const [, , removeCookie] = useCookies(['orderId', 'pg'])
  const { channel, web, aos, ios } = useChannel()
  const isPc = detectDevice() === 'pc'
  const mobileWeb = web && !isPc

  const resolvePaymentOS = (): PaymentOSType => {
    if (ios) return 'ios'
    if (aos) return 'aos'
    if (isPc) return 'pc'
    return 'mobile'
  }

  //결제 결과 요청
  const { mutateAsync: paymentWindowResponseCall } = useCustomMutation<
    PaymentWindowResponseSpace.Request,
    PaymentWindowResponseSpace.Response
  >(paymentWindowResponseQueryOption())

  const pgWindow = (pg: PGType, payMethod: string) => {
    switch (payMethod) {
      case 'MOBILE':
      case 'PAYCO':
        return 'danal' as PGType
      case 'TMONEY':
        return 'billgate' as PGType
      case 'SAMSUNGPAY':
      case 'KAKAOPAY':
      case 'CARD':
        return pg as PGType
      default:
        return 'billgate' as PGType
    }
  }

  //주문 1건당 한 번만 생성해서 MCRS 요청과 결제창 form이 같은 값을 쓰도록 한다
  const createOrderDate = () => DateFormat(now(), 'YYYYMMDDHHmmss')

  //결제창 요청 정보 저장값
  const paymentRequestData = ({
    pg,
    payMethod,
    itemNameSlice,
    orderId,
    couponData,
    isGift = false,
    orderDate,
  }: PaymentDataProps) =>
    ({
      ...{
        /**필수값**/
        PG: pgWindow(pg, payMethod),
        PayMethod: payMethod,
        ORDER_ID: orderId,
        ORDER_DATE: orderDate ?? '',
        AMOUNT: `${couponData?.SalePrice}`,
        RETURN_URL: `${
          isGift
            ? process.env.REACT_APP_PAYMENT_GIFT_RETURN_URL
            : process.env.REACT_APP_PAYMENT_RETURN_URL
        }/${pgWindow(pg, payMethod)}`,
        ITEM_CODE: couponNumber as string,
        ITEM_NAME: itemNameSlice
          ? `${limitStringByBytes(couponData?.CouponName as string)}..`
          : `${couponData?.CouponName}`,
        USER_ID: c_tel || decryptPhoneNumber(pNumber),
        RESERVED1: couponType,
        RESERVED2: `${couponData?.SalePrice}`,
        RESERVED3: (pay_channel || channel) as string,
        CouponIdx: Number(couponIdx),
        DiscountRate: couponData?.DiscountRate as number,
        MobileWeb: mobileWeb,
        OS: resolvePaymentOS(),
      },
      ...(pgWindow(pg, payMethod) === 'billgate'
        ? {
            /**빌게이트**/
            SERVICE_ID: payTypeToServiceId.find((w) => w?.id === payMethod)?.value as string,
            SERVICE_CODE: payMethod === 'TMONEY' ? '1600' : '0900',
            SERVICE_TYPE: '0000',
            USER_NAME: c_tel || decryptPhoneNumber(pNumber!),
            USER_EMAIL: '',
            CANCEL_FLAG: 'Y',
            WEBAPI_FLAG: 'Y',
            LOGO: '',
            INSTALLMENT_PERIOD: '',
            INSTALLMENT_PERIOD_FOCUS: '',
            CARD_TYPE: '',
            DIRECT_USE: '',
            TAX_FREE_AMOUNT: '',
            APPNAME: 'couponwallet',
            PAY_TYPE: payMethod === 'CARD' || payMethod === 'MOBILE' ? '' : payMethod,
          }
        : {
            /**다날**/
            Method: '',
            TransactionID: '',
            MerchantID: '',
            OrderName: '',
            SuccessURL: '',
            FailURL: '',
            UserEmail: '',
            UserName: c_tel || decryptPhoneNumber(pNumber),
            PaymentsMethod: '',
            TaxAmount: '',
            TaxFreeAmount: '',
            ServiceAmount: '',
            InstallmentMonths: '',
            CardCodes: '',
            NotiURL: '',
            ItemType: '',
            RegDT: '',
          }),
    }) as unknown as
      | PaymentWindowRequestCreditCardSpace.DanalRequest
      | PaymentWindowRequestCreditCardSpace.BillgateRequest

  //결제창 응답
  const handlePaymentResponse = (type: 'default' | 'gift', orderId: string) => {
    const c_idx = couponIdx ?? exception_Idx
    addFlowBreadcrumb('payment_confirm', 'request', {
      handler: 'paymentWindowResponseCall',
      type,
    })

    paymentWindowResponseCall({ OrderID: orderId }).then((res) => {
      const cancelPayment = res.Body?.ResultURL === ''
      addFlowBreadcrumb(cancelPayment ? 'payment_fail' : 'payment_confirm', 'response', {
        handler: 'paymentWindowResponseCall',
        type,
        errCode: res.Header.ErrCode,
        isCancel: cancelPayment,
      })

      switch (type) {
        case 'gift':
          if (cancelPayment) {
            giftPaymentCancelPageNav(couponType, c_idx, couponNumber, orderId) // 결제창 X 누른 경우
          } else {
            couponPaymentResultPageNav(res.Body?.ResultURL?.replace('/coupon/', '/gift/'), orderId)
          }
          break
        default:
          if (cancelPayment) {
            couponPaymentCancelPageNav(couponType, c_idx, couponNumber, orderId) // 결제창 X 누른 경우
          } else {
            couponPaymentResultPageNav(res.Body?.ResultURL, orderId)
          }
          break
      }

      removeCookie('pg', { path: '/' })
      removeCookie('orderId', { path: '/' })
    })
  }

  return { handlePaymentResponse, pgWindow, paymentRequestData, createOrderDate }
}

export default usePayment
